import React from "react";

/**
 * ProgressBar - An enterprise-grade, mobile-optimized progress bar component
 * 
 * @param {number} value - Current progress value
 * @param {number} max - Maximum value for progress calculation
 * @param {string} label - Optional label text
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {string} variant - Color variant: 'default', 'success', 'warning', 'danger'
 * @param {boolean} showPercentage - Whether to show percentage
 * @param {boolean} animated - Whether to animate the progress bar
 * @param {boolean} showValue - Whether to show current/max values
 */
const ProgressBar = ({ 
  value = 0, 
  max = 100, 
  label,
  size = 'md',
  variant = 'default',
  showPercentage = true,
  animated = true,
  showValue = false
}) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));

  const variants = {
    default: {
      gradient: 'from-blue-500 to-purple-500',
      glow: 'shadow-blue-500/20',
      text: 'text-blue-400'
    },
    success: {
      gradient: 'from-green-500 to-emerald-500',
      glow: 'shadow-green-500/20',
      text: 'text-green-400'
    },
    warning: {
      gradient: 'from-yellow-500 to-orange-500',
      glow: 'shadow-yellow-500/20',
      text: 'text-yellow-400'
    },
    danger: {
      gradient: 'from-red-500 to-rose-500',
      glow: 'shadow-red-500/20',
      text: 'text-red-400'
    }
  };

  const sizes = {
    sm: {
      height: 'h-2',
      text: 'text-xs'
    },
    md: {
      height: 'h-3',
      text: 'text-sm'
    },
    lg: {
      height: 'h-4',
      text: 'text-base'
    }
  };

  const variantStyles = variants[variant] || variants.default;
  const sizeStyles = sizes[size] || sizes.md;

  return (
    <div className="w-full">
      {/* Label and percentage/value */}
      {(label || showPercentage || showValue) && (
        <div className="flex justify-between items-center mb-2">
          {label && (
            <span className={`${sizeStyles.text} font-medium text-cream-900 dark:text-gray-300`}>
              {label}
            </span>
          )}

          <div className="flex items-center gap-3">
            {showValue && (
              <span className={`${sizeStyles.text} text-cream-700 dark:text-gray-400`}>
                {value} / {max}
              </span>
            )}
            
            {showPercentage && (
              <span className={`${sizeStyles.text} font-bold ${variantStyles.text}`}>
                {Math.round(pct)}%
              </span>
            )}
          </div>
        </div>
      )}

      {/* Progress bar container */}
      <div className={`
        w-full bg-cream-200 dark:bg-gray-700/50 rounded-full ${sizeStyles.height}
        overflow-hidden border border-cream-300 dark:border-gray-600/50 backdrop-blur-sm
        shadow-inner relative
      `}>
        {/* Background pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />

        {/* Progress fill */}
        <div
          className={`
            ${sizeStyles.height} rounded-full 
            bg-gradient-to-r ${variantStyles.gradient}
            transition-all duration-500 ease-out
            relative overflow-hidden
            ${animated ? 'animate-shimmer' : ''}
            shadow-lg ${variantStyles.glow}
          `}
          style={{ width: `${pct}%` }}
        >
          {/* Shine effect */}
          {animated && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-slide" />
          )}
        </div>

        {/* Glow effect underneath */}
        <div
          className={`
            absolute top-0 ${sizeStyles.height} rounded-full 
            bg-gradient-to-r ${variantStyles.gradient}
            blur-sm opacity-50 transition-all duration-500 ease-out
          `}
          style={{ width: `${pct}%` }}
        />
      </div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        @keyframes shimmer-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
        .animate-shimmer-slide {
          animation: shimmer-slide 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ProgressBar;